import React from "react";
import { Phone, Mail, Calendar, FileText, CheckSquare, Clock, User } from "lucide-react";
import UiText from "./UiText";

const ACTIVITY_TYPES = {
  call: { icon: Phone, color: "#10b981", label: "Call" },
  email: { icon: Mail, color: "#3b82f6", label: "Email" },
  meeting: { icon: Calendar, color: "#8b5cf6", label: "Meeting" },
  note: { icon: FileText, color: "#f59e0b", label: "Note" },
  task: { icon: CheckSquare, color: "#ef4444", label: "Task" },
};

const OUTCOME_LABELS = {
  successful: "✓ Successful",
  follow_up_required: "→ Follow-up Required",
  no_answer: "✗ No Answer",
  left_message: "💬 Left Message",
  not_interested: "✗ Not Interested",
  scheduled_meeting: "📅 Scheduled Meeting",
};

export default function RecentActivityTimeline({ activities, loading, onEdit, currentUserId, isAdmin }) {
  const canEdit = (activity) => {
    if (!currentUserId) return false;
    if (isAdmin) return true;
    return activity.created_by_id === currentUserId;
  };

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "3px solid #f3f4f6",
            borderTopColor: "#3b82f6",
            borderRadius: "50%",
            margin: "0 auto",
            animation: "spin 1s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div
        style={{
          padding: "3rem 1rem",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: "80px",
            height: "80px",
            margin: "0 auto 1rem",
            borderRadius: "50%",
            background: "#2563eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "36px",
          }}
        >
          📋
        </div>
        <UiText variant="section" as="h3" style={{ marginBottom: "0.5rem", color: "#374151" }}>
          No Activities Yet
        </UiText>
        <UiText variant="small" as="p" style={{ color: "#6b7280", maxWidth: "300px", margin: "0 auto" }}>
          Start tracking your interactions by logging your first activity
        </UiText>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    } else if (diffInHours < 48) {
      return "Yesterday";
    } else if (diffInHours < 168) {
      return date.toLocaleDateString("en-US", { weekday: "long" });
    } else {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div style={{ position: "relative" }}>
      {/* Timeline */}
      {activities.map((activity, index) => {
        const activityType = ACTIVITY_TYPES[activity.type] || ACTIVITY_TYPES.note;
        const Icon = activityType.icon;
        const isLast = index === activities.length - 1;
        const canEditActivity = canEdit(activity);

        return (
          <div
            key={activity.id}
            style={{
              position: "relative",
              paddingBottom: isLast ? "0" : "1.5rem",
              paddingLeft: "2.5rem",
            }}
          >
            {/* Timeline Line */}
            {!isLast && (
              <div
                style={{
                  position: "absolute",
                  left: "1rem",
                  top: "2.5rem",
                  bottom: "0",
                  width: "2px",
                  background: "linear-gradient(180deg, #e5e7eb 0%, #f3f4f6 100%)",
                }}
              />
            )}

            {/* Timeline Icon */}
            <div
              style={{
                position: "absolute",
                left: "0",
                top: "0.25rem",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "white",
                border: `3px solid ${activityType.color}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Icon size={14} color={activityType.color} />
            </div>

            {/* Activity Card */}
            <div
              style={{
                backgroundColor: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                padding: "1rem",
                transition: "all 0.2s",
                cursor: canEditActivity ? "pointer" : "default",
                position: "relative",
              }}
              onMouseOver={(e) => {
                if (!canEditActivity) return;
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.08)";
                e.currentTarget.style.borderColor = activityType.color;
              }}
              onMouseOut={(e) => {
                if (!canEditActivity) return;
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = "#e5e7eb";
              }}
              onClick={() => {
                if (canEditActivity && onEdit) {
                  onEdit(activity);
                }
              }}
              title={canEditActivity ? "Click to edit" : ""}
            >
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: "600",
                        color: activityType.color,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {activityType.label}
                    </span>
                    {activity.duration && (
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "#6b7280",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                        }}
                      >
                        <Clock size={12} /> {activity.duration} min
                      </span>
                    )}
                  </div>
                  <UiText variant="section" as="h4" style={{ marginBottom: "0.25rem", fontSize: "0.95rem" }}>
                    {activity.subject}
                  </UiText>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", fontSize: "0.75rem", color: "#6b7280" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                      <User size={12} /> {activity.created_by_name || "Unknown"}
                    </span>
                    <span>•</span>
                    <span title={formatFullDate(activity.date)}>{formatDate(activity.date)}</span>
                  </div>
                </div>

                <div style={{ width: "16px", height: "16px" }} />
              </div>

              {/* Description */}
              {activity.description && (
                <UiText
                  variant="small"
                  as="p"
                  style={{
                    color: "#4b5563",
                    lineHeight: "1.6",
                    marginBottom: activity.outcome ? "0.75rem" : "0",
                  }}
                >
                  {activity.description}
                </UiText>
              )}

              {/* Outcome Badge */}
              {activity.outcome && (
                <div style={{ marginTop: "0.75rem" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "0.25rem 0.75rem",
                      borderRadius: "6px",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                      backgroundColor: `${activityType.color}15`,
                      color: activityType.color,
                    }}
                  >
                    {OUTCOME_LABELS[activity.outcome] || activity.outcome}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
